import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { enqueue, isNetworkError } from "@/lib/offlineQueue";

export interface EpisodeNote {
  id: string;
  user_id: string;
  episode_id: string;
  content: string;
  timestamp_seconds: number | null;
  created_at: string;
  updated_at: string;
  episode?: {
    id: string;
    title: string;
    slug: string;
    audio_url: string | null;
    duration_seconds: number | null;
    podcast: {
      name: string;
      artwork_url: string | null;
    };
  };
}

/** Notes for a specific episode */
export function useEpisodeNotes(episodeId?: string) {
  const { user } = useAuth();
  const [notes, setNotes] = useState<EpisodeNote[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!user || !episodeId) return;
    setLoading(true);
    const { data } = await supabase
      .from("episode_notes")
      .select("*")
      .eq("user_id", user.id)
      .eq("episode_id", episodeId)
      .order("created_at", { ascending: true });
    setNotes((data as EpisodeNote[]) || []);
    setLoading(false);
  }, [user, episodeId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const addNote = useCallback(
    async (content: string, timestampSeconds?: number | null) => {
      if (!user || !episodeId) return null;
      const { data, error } = await supabase
        .from("episode_notes")
        .insert({
          user_id: user.id,
          episode_id: episodeId,
          content,
          timestamp_seconds: timestampSeconds ?? null,
        })
        .select()
        .single();
      if (error) {
        if (isNetworkError(error) || !navigator.onLine) {
          // Synthesize a local note and queue the insert. Server-side, the row
          // will get a new id; the temp will be replaced on the next refetch.
          const tempId = `temp-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
          const localNote: EpisodeNote = {
            id: tempId,
            user_id: user.id,
            episode_id: episodeId,
            content,
            timestamp_seconds: timestampSeconds ?? null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          setNotes((prev) => [...prev, localNote]);
          await enqueue({
            kind: "episode_note",
            payload: { op: "create", user_id: user.id, episode_id: episodeId, content, timestamp_seconds: timestampSeconds ?? null },
          });
          toast.message("Note saved offline — will sync");
          return localNote;
        }
        toast.error("Failed to save note");
        return null;
      }
      const note = data as EpisodeNote;
      setNotes((prev) => [...prev, note]);
      toast.success("Note saved");
      return note;
    },
    [user, episodeId]
  );

  const updateNote = useCallback(
    async (noteId: string, content: string, timestampSeconds?: number | null) => {
      const updates: { content: string; timestamp_seconds?: number | null } = { content };
      if (timestampSeconds !== undefined) {
        updates.timestamp_seconds = timestampSeconds;
      }
      const { error } = await supabase
        .from("episode_notes")
        .update(updates)
        .eq("id", noteId);
      if (error) {
        toast.error("Failed to update note");
        return;
      }
      setNotes((prev) =>
        prev.map((n) => (n.id === noteId ? { ...n, ...updates, updated_at: new Date().toISOString() } : n))
      );
      toast.success("Note updated");
    },
    []
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      const { error } = await supabase.from("episode_notes").delete().eq("id", noteId);
      if (error) {
        toast.error("Failed to delete note");
        return;
      }
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
    },
    []
  );

  return { notes, loading, addNote, updateNote, deleteNote, refetch: fetchNotes };
}

/** All notes for the current user across all episodes (for the library) */
export function useAllEpisodeNotes() {
  const { user } = useAuth();
  const [notes, setNotes] = useState<EpisodeNote[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setNotes([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("episode_notes")
      .select("*, episode:episodes(id, title, slug, audio_url, duration_seconds, podcast:podcasts(name, artwork_url))")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    setNotes((data as unknown as EpisodeNote[]) || []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateNote = useCallback(
    async (noteId: string, content: string) => {
      const { error } = await supabase
        .from("episode_notes")
        .update({ content })
        .eq("id", noteId);
      if (error) {
        toast.error("Failed to update note");
        return;
      }
      setNotes((prev) =>
        prev.map((n) => (n.id === noteId ? { ...n, content, updated_at: new Date().toISOString() } : n))
      );
      toast.success("Note updated");
    },
    []
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      const { error } = await supabase.from("episode_notes").delete().eq("id", noteId);
      if (error) {
        toast.error("Failed to delete note");
        return;
      }
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
    },
    []
  );

  return { notes, loading, updateNote, deleteNote, refetch: fetchAll };
}

/** Get note count for an episode (lightweight) */
export function useNoteCount(episodeId?: string) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user || !episodeId) return;
    supabase
      .from("episode_notes")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("episode_id", episodeId)
      .then(({ count: c }) => setCount(c || 0));
  }, [user, episodeId]);

  return count;
}
